import React from "react";
import {
  Archive,
  Code,
  File,
  FileAudio,
  FileCode2,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Image,
} from "lucide-react";

interface FileIconProps {
  name: string;
  type?: string;
  className?: string;
  size?: number;
}

export function getFileCategory(name: string, type: string = ""): {
  category: "image" | "video" | "audio" | "archive" | "code" | "document" | "sheet" | "generic";
  color: string;
  glow: string;
} {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  const mime = type.toLowerCase();

  if (mime.startsWith("image/") || ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "ico", "avif"].includes(ext)) {
    return { category: "image", color: "#7cff5c", glow: "rgba(124, 255, 92, 0.25)" };
  }
  if (mime.startsWith("video/") || ["mp4", "mkv", "mov", "avi", "webm", "flv", "m4v"].includes(ext)) {
    return { category: "video", color: "#5ce1ff", glow: "rgba(92, 225, 255, 0.25)" };
  }
  if (mime.startsWith("audio/") || ["mp3", "wav", "flac", "aac", "ogg", "m4a", "opus"].includes(ext)) {
    return { category: "audio", color: "#ffb85c", glow: "rgba(255, 184, 92, 0.25)" };
  }
  if (["zip", "tar", "gz", "tgz", "bz2", "7z", "rar", "xz"].includes(ext) || mime.includes("zip") || mime.includes("compressed")) {
    return { category: "archive", color: "#e35cff", glow: "rgba(227, 92, 255, 0.25)" };
  }
  if (["js", "ts", "jsx", "tsx", "py", "rs", "go", "c", "cpp", "h", "java", "json", "yaml", "yml", "toml", "css", "html", "sh", "sql"].includes(ext)) {
    return { category: "code", color: "#5c9dff", glow: "rgba(92, 157, 255, 0.25)" };
  }
  if (["csv", "tsv", "xls", "xlsx"].includes(ext)) {
    return { category: "sheet", color: "#5cffaa", glow: "rgba(92, 255, 170, 0.25)" };
  }
  if (["pdf", "doc", "docx", "txt", "md", "rtf"].includes(ext) || mime.startsWith("text/")) {
    return { category: "document", color: "#ffd15c", glow: "rgba(255, 209, 92, 0.25)" };
  }
  return { category: "generic", color: "#a3b8a3", glow: "rgba(163, 184, 163, 0.15)" };
}

export function FileIcon({ name, type = "", size = 18, className = "" }: FileIconProps) {
  const { category, color } = getFileCategory(name, type);

  const iconProps = { size, color, strokeWidth: 2, className };

  switch (category) {
    case "image":
      return <Image {...iconProps} />;
    case "video":
      return <FileVideo {...iconProps} />;
    case "audio":
      return <FileAudio {...iconProps} />;
    case "archive":
      return <Archive {...iconProps} />;
    case "code":
      return <FileCode2 {...iconProps} />;
    case "sheet":
      return <FileSpreadsheet {...iconProps} />;
    case "document":
      return <FileText {...iconProps} />;
    default:
      return <File {...iconProps} />;
  }
}
